import { useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

import AddIcon from '@mui/icons-material/Add'
import CircularProgress from '@mui/material/CircularProgress'
import Fab from '@mui/material/Fab'

import { Flex, FlexContainer } from 'components/Flex'
import ListAction from 'components/ListAction'
import ListFilter from 'components/ListFilter'
import ListItem from 'components/ListItem'
import ListSort from 'components/ListSort'
import Panel from 'components/Panel'
import { orderBy, useDocs, where } from 'lib/firestore'
import { Role } from 'utils/constant'

import MemberDialog from './MemberDialog'

export default function MemberList({ account }) {
  const { state } = useLocation()
  const [data, setData] = useState(null)
  const [sort, setSort] = useState({ field: 'role', order: 'asc' })
  const [filter, setFilter] = useState(() => {
    if (account.role === Role.LOCATION_MEMBER.value) {
      return { locationId: account.locationId, role: Role.LOCATION_MEMBER.value }
    }
    if (state && state.locationId) {
      return { locationId: state.locationId, role: '*' }
    }
    return { locationId: '*', role: '*' }
  })
  const conditions = useMemo(() => {
    const _conditions = []
    Object.entries(filter).forEach(([field, value]) => {
      if (value !== '*') {
        _conditions.push(where(field, '==', value))
      }
    })
    // role 絞り込み中に role ソートしない
    if (filter.role === '*' || sort.field !== 'role') {
      _conditions.push(orderBy(sort.field, sort.order))
    }
    return _conditions
  }, [filter, sort])
  const { items } = useDocs(`accounts/${account.id}/members`, ...conditions)
  const { items: _locations } = useDocs(
    `accounts/${account.id}/locations`,
    where('deleted', '==', false)
  )
  const locations = useMemo(() => {
    if (account.role === Role.LOCATION_MEMBER.value) {
      return _locations.filter(x => x.id === account.locationId)
    }
    return _locations
  }, [account, _locations])

  const handleChangeSort = field => {
    setSort(current => ({
      field,
      order: current.field !== field || current.order === 'desc' ? 'asc' : 'desc',
    }))
  }

  const handleChangeFilter = (field, value) => {
    const _filter = { ...filter }
    _filter[field] = value
    // 拠点選択時には role をリセット
    if (field === 'locationId' && value !== '*') {
      _filter.role = '*'
    }
    // 権限でアカウントオーナーもしくはアカウントメンバー選択時には locationId をリセット
    if (field === 'role' && value !== Role.LOCATION_MEMBER.value) {
      _filter.locationId = '*'
    }
    setFilter(_filter)
  }

  if (!items) {
    return (
      <FlexContainer style={{ alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </FlexContainer>
    )
  }

  return (
    <Flex>
      <ListAction>
        <ListFilter
          fields={[
            {
              id: 'locationId',
              name: '拠点',
              options: locations
                .sort((a, b) => {
                  a.category = a.category || ''
                  b.category = b.category || ''
                  return a.category === b.category
                    ? a.name > b.name
                      ? 1
                      : -1
                    : a.category && b.category
                    ? a.category > b.category
                      ? 1
                      : -1
                    : b.category
                    ? 1
                    : -1
                })
                .map(x => ({ label: x.name, value: x.id })),
              disabled: account.role === Role.LOCATION_MEMBER.value,
            },
            {
              id: 'role',
              name: '権限',
              options: Object.values(Role).map(x => ({ label: x.name, value: x.value })),
              disabled: account.role === Role.LOCATION_MEMBER.value,
            },
          ]}
          value={filter}
          onChange={handleChangeFilter}
        />
      </ListAction>
      <Panel>
        <ListSort
          fields={[
            { id: 'name', name: 'ユーザー名' },
            { id: 'role', name: '権限' },
          ]}
          value={sort}
          onChange={handleChangeSort}
        />
        {items.map(x => (
          <ListItem
            key={x.id}
            primary={x.name || x.email}
            secondary={Object.values(Role).find(({ value }) => value === x.role).name}
            label={x.uid === null ? '招待中' : null}
            onClick={() => setData(x)}
          />
        ))}
      </Panel>
      <Fab
        color="primary"
        style={{ position: 'fixed', right: 16, bottom: 16 }}
        onClick={() =>
          setData(
            account.role === Role.LOCATION_MEMBER.value
              ? { role: Role.LOCATION_MEMBER.value, locationId: account.locationId }
              : {}
          )
        }
      >
        <AddIcon />
      </Fab>
      {data && (
        <MemberDialog
          account={account}
          locations={locations}
          data={data}
          onClose={() => setData(null)}
        />
      )}
    </Flex>
  )
}
